// --------------------------------------------------------------
// Created On: 2021-10-25
// Author: Zachary Thomas
//
// Last Modified: 2023-09-08
// Modified By: Zachary Thomas
//
// Copyright 2023 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState } from "react";
import Spinner from "../../components/Spinner/Spinner";
import PasswordInput from "../../components/PasswordInput/PasswordInput";
import Error from "../../components/Error/Error";
import Card from "../../components/Card/Card";
import { useNavigate } from "react-router-dom";
import generateErrorMessage from "../../utilities/api/generateErrorMessage";
import apiRequest from "../../utilities/api/apiRequest";
import PasswordRequirements from "../../components/PasswordRequirements/PasswordRequirements";
import {
  API,
  MIN_PASSWORD_LENGTH,
  MAX_PASSWORD_LENGTH,
  VALID_PASSWORD_NUMBER_REGULAR_EXPRESSION,
  VALID_PASSWORD_LOWERCASE_REGULAR_EXPRESSION,
  VALID_PASSWORD_UPPERCASE_REGULAR_EXPRESSION,
  VALID_PASSWORD_SPECIAL_REGULAR_EXPRESSION,
} from "../../constants/miscellaneous";
import styles from "./ChangeForgottenPasswordPage.module.scss";

// Page for users to change a forgotten password.
export default function ChangeForgottenPasswordPage(): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [temporaryPassword, setTemporaryPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const navigate = useNavigate();

  // Check if the user pressed enter, if they did we attempt to change the password.
  function checkPressEnter(event: React.KeyboardEvent<HTMLDivElement>): void {
    if (event.key === "Enter") {
      changePassword(temporaryPassword, newPassword);
    }
  }

  // Validate the passwords.
  function passwordIsValid(): boolean {
    if (newPassword.length < MIN_PASSWORD_LENGTH || newPassword.length > MAX_PASSWORD_LENGTH) {
      setErrorMessage(
        `Your new password must be between ${MIN_PASSWORD_LENGTH} and ${MAX_PASSWORD_LENGTH} characters long.`
      );
      return false;
    } else if (!VALID_PASSWORD_NUMBER_REGULAR_EXPRESSION.test(newPassword)) {
      setErrorMessage("Your new password must include at least one number.");
      return false;
    } else if (!VALID_PASSWORD_LOWERCASE_REGULAR_EXPRESSION.test(newPassword)) {
      setErrorMessage("Your new password must include at least one lower case letter.");
      return false;
    } else if (!VALID_PASSWORD_UPPERCASE_REGULAR_EXPRESSION.test(newPassword)) {
      setErrorMessage("Your new password must include at least one upper case letter.");
      return false;
    } else if (!VALID_PASSWORD_SPECIAL_REGULAR_EXPRESSION.test(newPassword)) {
      setErrorMessage("Your new password must include at least one special character.");
      return false;
    } else if (newPassword !== confirmPassword) {
      setErrorMessage("Your new password does not match the confirmation password.");
      return false;
    } else {
      return true;
    }
  }

  // Attempt to change the temporary password to the new password.
  async function changePassword(temporaryPassword: string, newPassword: string): Promise<void> {
    if (passwordIsValid()) {
      const requestBody = {
        emailAddress: email,
        currentPassword: temporaryPassword,
        newPassword: newPassword,
      };

      setLoading(true);
      const [response, responseBody] = (await apiRequest(
        `${API}/registration/password`,
        "PUT",
        requestBody
      )) as [Response, ResponseBody];
      setLoading(false);

      if (response.ok && responseBody) {
        navigate("/login");
      } else {
        setErrorMessage(
          generateErrorMessage(responseBody, response.status, "Unable to change password.")
        );
      }
    }
  }

  return (
    <div className="p-4">
      <Spinner loading={loading} />

      <div className={`${styles.body} mx-auto`} onKeyPress={(e) => checkPressEnter(e)}>
        <Card title="Change Password">
          <div className="mx-4 my-3">
            {/* Password requirements. */}
            <PasswordRequirements />

            {/* Password form. */}
            <label className="font-weight-bold mt-2">Email</label>
            <input
              type="email"
              className="form-control mt-2 mb-4"
              id="email-text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <div className="mb-4">
              <label className="font-weight-bold mt-2">
                Temporary Password (sent to your email)
              </label>
              <PasswordInput
                password={temporaryPassword}
                onChangePassword={(temporaryPassword) => setTemporaryPassword(temporaryPassword)}
              />
            </div>

            <div className="mb-4">
              <label className="font-weight-bold mt-2">New Password</label>
              <PasswordInput
                password={newPassword}
                onChangePassword={(newPassword) => setNewPassword(newPassword)}
              />
            </div>

            <div className="mb-4">
              <label className="font-weight-bold mt-2">Confirm Password</label>
              <PasswordInput
                password={confirmPassword}
                onChangePassword={(confirmPassword) => setConfirmPassword(confirmPassword)}
              />
            </div>

            <div className="text-center my-4">
              <button
                type="submit"
                id="submit-login"
                className={`${styles.button} btn btn-primary`}
                onClick={() => changePassword(temporaryPassword, newPassword)}
              >
                Save Password
              </button>
            </div>

            <Error message={errorMessage} />
          </div>
        </Card>
      </div>
    </div>
  );
}

interface ResponseBody {
  message: string;
}
